import { useCallback } from 'react';
import { useUiStateStore } from 'src/stores/uiStateStore';
import { Size, Coords } from 'src/types';
import {
  getUnprojectedBounds as getUnprojectedBoundsUtil,
  getFitToViewParams as getFitToViewParamsUtil,
  CoordsUtils
} from 'src/utils';
import { useScene } from 'src/hooks/useScene';
import { useResizeObserver } from './useResizeObserver';
import { FIT_TO_VIEW_SCREEN_PADDING } from 'src/config';

interface FitToViewInsets {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}

export const useDiagramUtils = () => {
  const scene = useScene();
  const rendererEl = useUiStateStore((state) => {
    return state.rendererEl;
  });
  const { size: rendererSize } = useResizeObserver(rendererEl);
  const uiStateActions = useUiStateStore((state) => {
    return state.actions;
  });

  const getUnprojectedBounds = useCallback((): Size & Coords => {
    return getUnprojectedBoundsUtil(scene.currentView);
  }, [scene.currentView]);

  const getFitToViewParams = useCallback(
    (viewportSize: Size) => {
      return getFitToViewParamsUtil(scene.currentView, viewportSize);
    },
    [scene.currentView]
  );

  const fitToView = useCallback(async (insets: FitToViewInsets = {}) => {
    const left = Math.max(insets.left ?? 0, 0);
    const right = Math.max(insets.right ?? 0, 0);
    const top = Math.max(insets.top ?? 0, 0);
    const bottom = Math.max(insets.bottom ?? 0, 0);
    const visibleSize = {
      width: Math.max(
        rendererSize.width - left - right - FIT_TO_VIEW_SCREEN_PADDING * 2,
        1
      ),
      height: Math.max(
        rendererSize.height - top - bottom - FIT_TO_VIEW_SCREEN_PADDING * 2,
        1
      )
    };
    const { zoom, scroll } = getFitToViewParams(visibleSize);

    uiStateActions.setScroll({
      position: {
        // Scene layers are centered in the full renderer. Shift the fitted
        // content to the center of the unobscured area between editor panels.
        x: scroll.x + (left - right) / 2,
        y: scroll.y + (top - bottom) / 2
      },
      offset: CoordsUtils.zero()
    });
    uiStateActions.setZoom(zoom);
  }, [uiStateActions, getFitToViewParams, rendererSize]);

  return {
    getUnprojectedBounds,
    fitToView,
    getFitToViewParams
  };
};
